/**
 * Barra lateral. 262px fijos, sticky a 100vh en escritorio.
 *
 * ADAPTABLE EN TRES ESCALONES, y cada uno responde a un problema distinto:
 *
 *   · ≥1200px — como el maquetado: 262px con iconos y etiquetas.
 *   · 900–1200px — RAÍL DE ICONOS de 72px. Se recuperan 190px de ancho para las tablas, que
 *     es donde de verdad hacen falta. La etiqueta no se pierde: sigue en el `title` y en el
 *     nombre accesible del enlace.
 *   · <900px — PANEL DESLIZANTE sobre el contenido, con velo y cierre con Esc. A ese ancho, una
 *     barra fija se come media pantalla.
 *
 * En el panel deslizante, pulsar un enlace lo CIERRA. Es la diferencia entre una navegación
 * que funciona y una que deja al usuario mirando el menú encima de la pantalla a la que
 * acaba de ir.
 *
 * SEIS ENTRADAS, NO ONCE. Arriba, donde se trabaja: Inicio, Propiedades, Inquilinos,
 * Ingresos y gastos y Documentos. Debajo, separado, «Seguimiento»: las cinco secciones que
 * solo informan (Cobros, Movimientos del banco, Mantenimiento, Informes y la vista del
 * inquilino) unificadas en una página con pestañas.
 *
 * PENDIENTES, SEGUNDA. Es la lista de lo que hay que subir o hacer, y absorbió a las alertas
 * (antes una campana en la barra superior). Lleva su contador: lo vencido y lo de esta semana,
 * que es lo que pide atención; lo de más adelante no tiene por qué encender nada.
 * Abajo, pegada con `margin-top:auto`, la tarjeta de usuario, que es el desplegable desde el
 * que se cierra la sesión.
 */
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, queryKeys } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { AccountMenu } from './AccountMenu';
import { Icon, type IconName } from './Icon';
import mark from '../assets/naar-mark.png';
import logoRealState from '../assets/naar-real-state.png';
import styles from './Sidebar.module.css';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
}

/** Donde se trabaja: se da de alta, se edita, se sube. */
const PRIMARY_NAV: NavItem[] = [
  { to: '/', label: 'Inicio', icon: 'home' },
  { to: '/propiedades', label: 'Propiedades', icon: 'building' },
  { to: '/inquilinos', label: 'Inquilinos', icon: 'users' },
  // Aquí entran las facturas y se cierra el mes: es trabajo, no consulta.
  { to: '/ingresos-y-gastos', label: 'Ingresos y gastos', icon: 'cash-flow' },
  // Luz, agua, internet… de cada piso, y la regularización de cada cuatrimestre.
  { to: '/suministros', label: 'Suministros', icon: 'bolt' },
  { to: '/documentos', label: 'Documentos', icon: 'file' },
];

/** Donde se consulta: las cinco secciones informativas, juntas en una. */
const SECONDARY_NAV: NavItem[] = [
  { to: '/seguimiento', label: 'Seguimiento', icon: 'chart' },
  // Un chat que contesta consultando los datos: «¿cuándo vence el contrato de…?».
  { to: '/asistente', label: 'Asistente IA', icon: 'chat' },
];

function NavItemLink({
  item,
  onNavigate,
  badge,
}: {
  item: NavItem;
  onNavigate?: () => void;
  badge?: number;
}) {
  return (
    <NavLink
      to={item.to}
      // `end` solo en la raíz: si no, «Inicio» quedaría activo en todas las rutas.
      end={item.to === '/'}
      className={({ isActive }) =>
        isActive ? `${styles.item} ${styles.itemActive}` : styles.item
      }
      onClick={onNavigate}
      // En el raíl de iconos la etiqueta no se ve; el nombre accesible NO se pierde nunca.
      title={badge ? `${item.label} · ${badge} para ya` : item.label}
    >
      <Icon name={item.icon} size={19} />
      <span className={styles.itemLabel}>{item.label}</span>
      {badge ? (
        <span className={styles.badge} aria-label={`${badge} para ya`}>
          {badge > 99 ? '99+' : badge}
        </span>
      ) : null}
    </NavLink>
  );
}

interface SidebarProps {
  /** Solo tiene efecto en el escalón de panel deslizante. */
  open?: boolean;
  onNavigate?: () => void;
}

export function Sidebar({ open = false, onNavigate }: SidebarProps) {
  const { user } = useAuth();
  /*
   * El contador de Pendientes. Comparte caché con la pantalla, y se refresca cada pocos minutos:
   * subir un ticket en otra pestaña tiene que bajarlo sin recargar.
   */
  const pending = useQuery({
    queryKey: queryKeys.pending(),
    queryFn: () => api.pending(),
    enabled: Boolean(user) && user?.role !== 'tenant',
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
  const urgentes = pending.data ? pending.data.counts.vencido + pending.data.counts.semana : 0;

  return (
    <aside className={open ? `${styles.sidebar} ${styles.open}` : styles.sidebar}>
      {/*
        Marca: el logo de NAAR Real State, con el nombre. En el raíl estrecho (entre 900 y
        1200 px) no cabe el texto y queda solo el pico, el mismo de la pestaña del navegador.
      */}
      <div className={styles.logoWrap}>
        <img
          src={logoRealState}
          alt="NAAR Real State"
          className={`${styles.logo} ${styles.logoFull}`}
          width={720}
          height={376}
        />
        <img src={mark} alt="NAAR" className={`${styles.logo} ${styles.logoMark}`} width={640} height={344} />
      </div>

      <nav className={styles.nav} aria-label="Navegación principal" data-tour="nav">
        {PRIMARY_NAV.map((item) => (
          <NavItemLink
            key={item.to}
            item={item}
            onNavigate={onNavigate}
          />
        ))}
        <hr className={styles.separator} />
        <span className={styles.groupLabel}>Para consultar</span>
        {SECONDARY_NAV.map((item) => (
          <NavItemLink
            key={item.to}
            item={item}
            onNavigate={onNavigate}
            // Pendientes vive en Seguimiento: su contador va aquí.
            {...(item.to === '/seguimiento' && urgentes > 0 ? { badge: urgentes } : {})}
          />
        ))}
      </nav>

      {/* Cuenta y cierre de sesión. Ver `AccountMenu`. */}
      <AccountMenu />
    </aside>
  );
}
